import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { FileText, Loader2, Filter, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import AdminHeader from './AdminHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('kaha_session');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const STATUS_BADGES = {
  pending: { label: 'Pendiente', icon: Clock, cls: 'bg-amber-100 text-amber-800' },
  generated: { label: 'Generada', icon: CheckCircle, cls: 'bg-green-100 text-green-800' },
  sent: { label: 'Enviada', icon: CheckCircle, cls: 'bg-blue-100 text-blue-800' },
  error: { label: 'Error', icon: AlertCircle, cls: 'bg-red-100 text-red-800' },
};

const AdminInvoices = ({ adminUser }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const res = await axios.get(`${API}/admin/invoices${params}`, { headers: getAuthHeaders() });
      setInvoices(res.data);
    } catch {
      toast.error('Error al cargar facturas');
    }
    setLoading(false);
  };

  useEffect(() => { fetchInvoices(); }, [filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    try {
      await axios.put(`${API}/admin/invoices/${orderId}/status`, { status: newStatus }, { headers: getAuthHeaders() });
      toast.success(`Status actualizado a "${newStatus}"`);
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
    setUpdatingId(null);
  };

  const exportCSV = () => {
    const headers = ['Folio', 'Cliente', 'RFC', 'Razón Social', 'Email', 'Status', 'Total', 'Fecha'];
    const rows = invoices.map(inv => [
      inv.order_number,
      inv.customer_name,
      inv.invoice_data?.rfc || '',
      inv.invoice_data?.name || '',
      inv.invoice_data?.email || '',
      inv.invoice_status,
      inv.total_amount,
      inv.invoice_requested_at ? new Date(inv.invoice_requested_at).toLocaleDateString('es-MX') : '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `facturas_kaha_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader adminUser={adminUser} currentPage="facturas" />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-primary">Facturación</h1>
            <p className="text-muted-foreground text-sm">{invoices.length} solicitudes</p>
          </div>
          <div className="flex gap-3">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-44" data-testid="filter-invoice-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendiente</SelectItem>
                <SelectItem value="generated">Generada</SelectItem>
                <SelectItem value="sent">Enviada</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={exportCSV} data-testid="btn-export-csv">
              Exportar CSV
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No hay solicitudes de factura {filterStatus !== 'all' ? `con status "${filterStatus}"` : ''}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {invoices.map((inv) => {
              const badge = STATUS_BADGES[inv.invoice_status] || STATUS_BADGES.pending;
              const BadgeIcon = badge.icon;
              return (
                <Card key={inv.id} className="shadow-soft" data-testid={`invoice-row-${inv.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-mono font-bold text-sm">{inv.order_number}</span>
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>
                            <BadgeIcon className="w-3 h-3" />
                            {badge.label}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground space-x-4">
                          <span>{inv.invoice_data?.name || inv.customer_name}</span>
                          <span className="font-mono">{inv.invoice_data?.rfc}</span>
                          <span>{inv.invoice_data?.email}</span>
                        </div>
                      </div>

                      <div className="text-right text-sm">
                        <p className="font-bold">${inv.total_amount?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</p>
                        <p className="text-xs text-muted-foreground">
                          {inv.invoice_requested_at ? new Date(inv.invoice_requested_at).toLocaleDateString('es-MX') : '-'}
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <Select
                          value={inv.invoice_status}
                          onValueChange={(v) => updateStatus(inv.id, v)}
                          disabled={updatingId === inv.id}
                        >
                          <SelectTrigger className="w-36 h-8 text-xs" data-testid={`select-status-${inv.id}`}>
                            {updatingId === inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <SelectValue />}
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pendiente</SelectItem>
                            <SelectItem value="generated">Facturada</SelectItem>
                            <SelectItem value="sent">Enviada</SelectItem>
                            <SelectItem value="error">Error</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminInvoices;
